import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from 'react-router-dom';
import { message as antdMessage } from 'antd';
import { Box, Typography, Container, Alert, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Chip, IconButton, Tooltip, FormHelperText } from '@mui/material';
import { Fade } from '@mui/material';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import sdkApi from '../../../shared/utils/sdkApi';
import GracePeriodWarning from '../../../shared/components/analytics/GracePeriodWarning';
import { useTeam } from '../../../shared/context/teamContext';
import { useSubscription } from '../../../shared/context/subscriptionContext';
import { getChainId } from '../../../shared/utils/chainIdMapper';
import { 
  Plus, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  ExternalLink,
  Settings,
  Link,
  Unlink,
  Key,
  Copy,
  Edit,
  FileText,
  Loader2
} from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import { getEmbedScriptSnippet } from '../../../shared/config/apiConfig';
import { MdOutlineAdsClick } from 'react-icons/md';
import { CiServer } from 'react-icons/ci';
import { motion } from 'framer-motion';

// Minimal Black Theme Design System
const styles = {
  primarycolor: "#0f172a", // Pure Black
  secondarycolor: "#0f172a", // Pure Black
  backgroundColor: "#ffffff", // Pure white background
  cardBg: "white",
  textPrimary: "#0f172a", // Primary text
  textSecondary: "#666666", // Gray for secondary text
  borderColor: "#E5E7EB", // Light gray borders
  headingFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 600 },
  bodyFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 400 },
  cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  cardShadowHover: "0 4px 16px rgba(0, 0, 0, 0.1)"
};

// Backend processing configuration (simplified)
const BACKEND_CONFIG = {
  TIMEOUT: 600000, // 10 minutes for backend processing
  TRANSACTION_LIMITS: {
    'Soneium': 250000,     // 1M transactions (BlockScout)
    'default': 150000       // 150k transactions (Etherscan V2)
  }
};

const IntegrationManager = ({ onMenuClick, onClose, screenSize, mode }) => {
  // mode: undefined = full with tabs, 'websites-only' = websites page only, 'smart-contracts-only' = smart contracts page only
  const { selectedTeam, isLoading: teamLoading } = useTeam();
  const { getPageAccess } = useSubscription();
  
  // Get location to read URL parameters
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  
  // State declarations
  const [websiteArray, setWebsiteArray] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddWebsiteModal, setShowAddWebsiteModal] = useState(false);
  const [newWebsiteDomain, setNewWebsiteDomain] = useState('');
  const [newWebsiteName, setNewWebsiteName] = useState('');
  const [domainProtocol, setDomainProtocol] = useState('https');
  const [scriptModal, setScriptModal] = useState(false);
  const [scriptCode, setScriptCode] = useState('');
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const defaultTabOrder = useMemo(() => ['websites', 'smart-contracts'], []);
  const integrationsAccess = getPageAccess?.('managewebsites') || {};
  const allowedTabs = integrationsAccess?.tabs;
  const visibleTabs = useMemo(() => {
    if (mode === 'websites-only') return ['websites'];
    if (mode === 'smart-contracts-only') return ['smart-contracts'];
    if (!Array.isArray(allowedTabs) || allowedTabs.length === 0) {
      return defaultTabOrder;
    }
    return defaultTabOrder.filter(tab => allowedTabs.includes(tab));
  }, [mode, allowedTabs, defaultTabOrder]);

  const [activeTab, setActiveTab] = useState(
    mode === 'smart-contracts-only' ? 'smart-contracts' : mode === 'websites-only' ? 'websites' : (tabParam || 'websites')
  );
  const [gtmCode, setGtmCode] = useState('');
  const [dataLayerCode, setDataLayerCode] = useState('');
  
  // Add subscription grace period state
  const [inGracePeriod, setInGracePeriod] = useState(false);
  const [gracePeriodInfo, setGracePeriodInfo] = useState(null);

  const [activeScriptTab, setActiveScriptTab] = useState('cdn');

  // Smart Contract states
  const [smartContracts, setSmartContracts] = useState([]);
  const [showAddContractModal, setShowAddContractModal] = useState(false);
  const [contractAddress, setContractAddress] = useState('');
  const [contractName, setContractName] = useState('');
  const [tokenSymbol, setTokenSymbol] = useState('');
  const [blockchain, setBlockchain] = useState('Ethereum');
  const [contractType, setContractType] = useState('ERC-20');
  const [customDecimals, setCustomDecimals] = useState('18');
  const [contractError, setContractError] = useState('');
  const [addingContract, setAddingContract] = useState(false);
  const [isFetchingTransactions, setIsFetchingTransactions] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 100 });
  const [processingStep, setProcessingStep] = useState('');
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteWebsiteModalVisible, setDeleteWebsiteModalVisible] = useState(false);
  const [websiteToDelete, setWebsiteToDelete] = useState(null);
  const [deletingWebsite, setDeletingWebsite] = useState(false);
  
  // Track if we're currently fetching to prevent duplicate requests
  const isFetchingRef = useRef(false);
  
  // Update active tab when URL parameter changes
  useEffect(() => {
    if (tabParam && visibleTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam, visibleTabs]);

  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [visibleTabs, activeTab]);
  
  // Clear any stale cache on initial mount to ensure fresh data loads
  useEffect(() => {
    if (selectedTeam?.name) {
      const cacheKey = `integration_fetch_${selectedTeam.name}`;
      const lastFetch = sessionStorage.getItem(cacheKey);
      if (lastFetch) {
        const age = Date.now() - parseInt(lastFetch);
      }
    }
  }, []);

  // Fetch data when component mounts and team is loaded
  useEffect(() => {
    if (teamLoading) {
      return;
    }
    
    if (!selectedTeam?.name) {
      return;
    }
    
    // Check if we've recently fetched data to prevent redundant calls
    const cacheKey = `integration_fetch_${selectedTeam.name}`;
    const lastFetch = sessionStorage.getItem(cacheKey);
    const hasData = websiteArray.length > 0 || smartContracts.length > 0;
    const cacheExpired = !lastFetch || (Date.now() - parseInt(lastFetch) > 5 * 60 * 1000);
    const shouldFetch = (cacheExpired || !hasData) && !isFetchingRef.current; // Force fetch if cache expired OR no data exists, and not already fetching
    
    if (shouldFetch) {
      isFetchingRef.current = true;
    fetchWebsites();
    fetchSmartContracts();
    checkSubscriptionStatus();
      sessionStorage.setItem(cacheKey, Date.now().toString());
      // Reset fetching flag after a delay to allow for retries if needed
      setTimeout(() => { isFetchingRef.current = false; }, 2000);
    } else {
    }
  }, [teamLoading, selectedTeam?.name, websiteArray.length, smartContracts.length]); // Check data state to trigger fetch if empty



  // Check subscription status
  const checkSubscriptionStatus = async () => {
    if (!selectedTeam?.name) return;
    
    try {
      const response = await axiosInstance.get(`/team/subscription-status/${selectedTeam.name}`);
      if (response.data.inGracePeriod) {
        setInGracePeriod(true);
        setGracePeriodInfo(response.data.gracePeriodInfo);
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  // Fetch websites
  const fetchWebsites = async () => {
    if (!selectedTeam?.name) return;
    
    setIsLoading(true);
    try {
      const candidate = selectedTeam?.name || selectedTeam;
      const isObjectId = typeof candidate === 'string' && /^[a-fA-F0-9]{24}$/.test(candidate);
      const response = isObjectId
        ? await axiosInstance.get(`/website/team/${candidate}`)
        : await axiosInstance.get(`/website/name/${candidate}`);
      if (response.data && response.data.websites) {
        setWebsiteArray(response.data.websites);
      }
    } catch (error) {
      console.error('Error fetching websites:', error);
      setError('Failed to load websites');
    } finally {
      setIsLoading(false);
    }
  };


  // Add website
  const handleAddWebsite = async () => {
    const appNameValid = /^[a-zA-Z0-9\s\-]+$/.test(newWebsiteName.trim());
    if (!newWebsiteName.trim()) {
      setMessage('App Name is required');
      setMessageType('error');
      return;
    }
    if (!appNameValid) {
      setMessage('App Name can only contain letters, numbers, spaces, and hyphens');
      setMessageType('error');
      return;
    }
    if (newWebsiteName.trim().length > 50) {
      setMessage('App Name must be 50 characters or less');
      setMessageType('error');
      return;
    }
    const domainTrimmed = newWebsiteDomain.trim();
    if (!domainTrimmed) {
      setMessage('Website Domain is required');
      setMessageType('error');
      return;
    }
    if (/[\/\?\#]/.test(domainTrimmed)) {
      setMessage('Domain cannot contain path or query parameters');
      setMessageType('error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosInstance.post('/website/create', {
        Domain: newWebsiteDomain.trim(),
        Name: newWebsiteName.trim(),
        teamName: selectedTeam.name
      });

      if (response.data.message) {
        setMessage('Website added successfully!');
        setMessageType('success');
        setNewWebsiteDomain('');
        setNewWebsiteName('');
        setDomainProtocol('https');
        setShowAddWebsiteModal(false);
        
        // Fetch updated websites and show script modal for the new website
        await fetchWebsites();
        
        // Find the newly added website and show script modal
        const newWebsite = response.data.website;
        if (newWebsite) {
          setSelectedWebsite(newWebsite);
          setScriptModal(true);
        }
      }
    } catch (error) {
      console.error('Error adding website:', error);
      setMessage(error.response?.data?.message || 'Failed to add website');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete website - open modal
  const handleDeleteWebsite = (siteId) => {
    const website = websiteArray.find(w => 
      (w.site_id || w.siteId) === siteId
    );
    if (website) {
      setWebsiteToDelete(website);
      setDeleteWebsiteModalVisible(true);
    }
  };

  // Confirm website delete
  const handleDeleteWebsiteConfirm = async () => {
    if (!websiteToDelete) return;
    
    const siteId = websiteToDelete.site_id || websiteToDelete.siteId;
    
    setDeletingWebsite(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await axiosInstance.delete(`/website/delete/${siteId}`);
      if (response.data.message) {
        antdMessage.success('Website deleted successfully!');
        setDeleteWebsiteModalVisible(false);
        setWebsiteToDelete(null);
        await fetchWebsites();
      } else {
        throw new Error('No confirmation message in response');
      }
    } catch (error) {
      console.error('Error deleting website:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete website';
      antdMessage.error(errorMessage);
    } finally {
      setDeletingWebsite(false);
    }
  };

  // Cancel website delete
  const handleDeleteWebsiteCancel = () => {
    setDeleteWebsiteModalVisible(false);
    setWebsiteToDelete(null);
  };

  // Verify website
  const handleVerifyWebsite = async (siteId) => {
    setVerifyLoading(true);
    try {
      // First get the website details to get the domain
      const website = websiteArray.find(w => w.siteId === siteId);
      if (!website) {
        setMessage('Website not found');
        setMessageType('error');
        return;
      }
      
      const response = await axiosInstance.post('/website/verify', {
        Domain: website.domain || website.Domain,
        siteId: siteId
      });
      if (response.data.message) {
        setMessage('Website verified successfully!');
        setMessageType('success');
        fetchWebsites();
      }
    } catch (error) {
      console.error('Error verifying website:', error);
      setMessage(error.response?.data?.message || 'Failed to verify website');
      setMessageType('error');
    } finally {
      setVerifyLoading(false);
    }
  };


  // Fetch smart contracts
  const fetchSmartContracts = async () => {
    if (!selectedTeam?.name) {
      console.log('No team selected, skipping smart contract fetch');
      return;
    }
    
    try {
      const response = await axiosInstance.get(`/smart-contracts/team/${selectedTeam.name}`);
      if (response.data && response.data.contracts) {
        setSmartContracts(response.data.contracts);
      }
    } catch (error) {
      console.error('Error fetching smart contracts:', error);
      setError('Failed to load smart contracts');
    }
  };

  // Add smart contract
  const handleAddSmartContract = async () => {
    if (!contractAddress.trim() || !contractName.trim()) {
      setMessage('Please fill in contract address and name');
      setMessageType('error');
      return;
    }

    // Validate custom decimals when using "Other" contract type
    if (contractType === 'Other') {
      const parsedDecimals = parseInt(customDecimals, 10);
      if (Number.isNaN(parsedDecimals) || parsedDecimals < 0) {
        setContractError('Please enter a valid non-negative number for custom decimal places.');
        setMessage('Please enter a valid non-negative number for custom decimal places.');
        setMessageType('error');
        return;
      }
    }

    setAddingContract(true);
    setContractError('');
    setLoadingStatus('Adding smart contract...');
    setLoadingProgress({ current: 10, total: 100 });

    try {
      // Save contract to database
      setLoadingStatus('Saving contract to database...');
      setLoadingProgress({ current: 30, total: 100 });
      
      // Map blockchain name to chain ID
      const chainId = getChainId(blockchain);
      if (!chainId) {
        setContractError(`Unsupported blockchain: ${blockchain}`);
        setMessage(`Unsupported blockchain: ${blockchain}`);
        setMessageType('error');
        return;
      }

      // Map contract type and custom decimals (actual decimal logic handled server-side)
      let contractTypeValue = contractType || 'ERC-20';
      let customDecimalsValue = undefined;
      if (contractType === 'Other') {
        const parsedDecimals = parseInt(customDecimals, 10);
        customDecimalsValue = Number.isNaN(parsedDecimals) ? undefined : parsedDecimals;
      }

      const response = await axiosInstance.post('/smart-contracts', {
        teamName: selectedTeam.name,
        contractAddress: contractAddress.trim(),
        chain: chainId,
        contractName: contractName.trim(),
        contractSymbol: tokenSymbol.trim() || '',
        contractType: contractTypeValue,
        customDecimals: customDecimalsValue
      });

      if (response.data && response.data.contract) {
        const newContract = response.data.contract;
        
        setLoadingStatus('Contract saved successfully. Fetching initial transactions...');
        setLoadingProgress({ current: 50, total: 100 });
        
        // Add to local state
        setSmartContracts(prev => [...prev, newContract]);
        
        // Close modal immediately
        setShowAddContractModal(false);
        resetContractForm();
        
        // Refresh contract list after delays to catch status updates
        // First refresh after 1 second (to catch pending -> fetching transition)
        setTimeout(() => {
          fetchSmartContracts();
        }, 1000);
        // Second refresh after 3 seconds (to catch any further updates)
        setTimeout(() => {
          fetchSmartContracts();
        }, 3000);
        
        setMessage('Smart contract added successfully!');
        setMessageType('success');
      }
    } catch (error) {
      console.error('Error adding smart contract:', error);
      setContractError(error.response?.data?.message || 'Failed to add smart contract');
      setMessage(error.response?.data?.message || 'Failed to add smart contract');
      setMessageType('error');
    } finally {
      setAddingContract(false);
      setIsFetchingTransactions(false);
      setLoadingStatus('');
      setLoadingProgress({ current: 0, total: 100 });
    }
  };

  // Poll for contract status updates when contracts are pending or fetching
  useEffect(() => {
    const hasActiveContracts = smartContracts.some(
      contract => contract.fetch_status === 'pending' || contract.fetch_status === 'fetching'
    );
    
    if (!hasActiveContracts || !selectedTeam?.name) {
      return; // No need to poll if no active contracts or no team selected
    }
    
    // Poll every 7 seconds to check for status updates
    const pollInterval = setInterval(() => {
      fetchSmartContracts();
    }, 7000);
    
    return () => clearInterval(pollInterval);
  }, [smartContracts, selectedTeam?.name]);

  // Connect to progress updates via SSE
  useEffect(() => {
    const eventSources = [];
    
    if (smartContracts.length > 0) {
      smartContracts.forEach(contract => {
        if (contract.fetch_status === 'fetching' || contract.fetch_status === 'pending') {
          // Get base URL for SSE
          const baseURL = process.env.REACT_APP_API_SERVER_URL || 'http://localhost:3002';
          const contractId = contract.contractId || contract.id;
          
          // Connect to SSE for progress updates
          const eventSource = new EventSource(`${baseURL}/api/smart-contracts/progress/${contractId}`, {
            withCredentials: false
          });
          
          eventSource.onmessage = (event) => {
            try {
              const progressData = JSON.parse(event.data);
              
              // Refresh contract list periodically during fetching to catch status changes
              if (progressData.status && progressData.status !== 'Connected') {
                // Refresh every 5 seconds during active fetching
                setTimeout(() => {
                  fetchSmartContracts();
                }, 5000);
              }
              
              if (progressData.status === 'Complete' || progressData.status === 'Stopped') {
                eventSource.close();
                // Refresh immediately when complete
                fetchSmartContracts();
              }
            } catch (error) {
              console.error('Error parsing progress data:', error);
            }
          };
          
          eventSource.onerror = (error) => {
            console.error('SSE connection error:', error);
            eventSource.close();
          };
          
          eventSources.push(eventSource);
        }
      });
    }
    
    // Cleanup function
    return () => {
      eventSources.forEach(eventSource => {
        eventSource.close();
      });
    };
  }, [smartContracts]);

  // Refresh contract (fetch new transactions)
  const fetchUpdatedTransactions = async (contract) => {
    if (!contract || !contract.contractId) {
      console.error('Invalid contract provided for refresh');
      return;
    }

    try {
      console.log(`🔄 Refreshing contract ${contract.contractId}`);
      
      await axiosInstance.post(`/smart-contracts/${contract.contractId || contract.id}/refresh`);
      
      // Refresh the contract list after a delay
      setTimeout(() => {
        fetchSmartContracts();
      }, 2000);
    } catch (error) {
      console.error('❌ Refresh failed:', error);
      setMessage(error.response?.data?.message || 'Failed to refresh contract');
      setMessageType('error');
    }
  };

  // Delete smart contract - open modal
  const handleDeleteSmartContract = (contractId) => {
    const contract = smartContracts.find(c => 
      (c.contractId || c._id || c.id) === contractId
    );
    if (contract) {
      setContractToDelete({
        ...contract,
        isFetching: contract && isFetchingTransactions
      });
      setDeleteModalVisible(true);
    }
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (!contractToDelete) return;
    
    const contractId = contractToDelete.contractId || contractToDelete._id || contractToDelete.id;
    const isFetching = contractToDelete.isFetching || false;
    
    setDeleting(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await axiosInstance.delete(`/smart-contracts/${contractId}`);
      
      if (response.data.message) {
        antdMessage.success('Smart contract deleted successfully!');
        setDeleteModalVisible(false);
        setContractToDelete(null);
        
        // Clear progress tracking for deleted contract
        try {
          const savedProgress = localStorage.getItem('contractProgress');
          if (savedProgress) {
            try {
              const progressData = JSON.parse(savedProgress);
              if (progressData.contractId === contractId) {
                localStorage.removeItem('contractProgress');
              }
            } catch (error) {
              console.error('Error clearing progress for deleted contract:', error);
            }
          }
        } catch (error) {
          console.error('Error clearing progress:', error);
        }
        
        // Refresh the contract list
        await fetchSmartContracts();
        
        // If this was the contract being fetched, stop the fetch
        if (isFetching) {
          setIsFetchingTransactions(false);
          setLoadingStatus('');
        }
      } else {
        throw new Error('No confirmation message in response');
      }
    } catch (error) {
      console.error('Error deleting smart contract:', error);
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || 'Failed to delete smart contract. Please try again.';
      antdMessage.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  // Cancel delete
  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setContractToDelete(null);
  };

  // Reset contract form
  const resetContractForm = () => {
    setContractAddress('');
    setContractName('');
    setTokenSymbol('');
    setBlockchain('Ethereum');
    setContractType('ERC-20');
    setCustomDecimals('18');
    setContractError('');
  };

  // Get blockchain icon
  const getBlockchainIcon = (blockchain) => {
    switch (blockchain.toLowerCase()) {
      case 'ethereum':
        return '⟠';
      case 'polygon':
        return '⬟';
      case 'bsc':
      case 'binance':
        return '◉';
      case 'base':
        return '⬢';
      case 'arbitrum':
        return '◈';
      case 'optimism':
        return '◈';
      case 'avalanche c':
        return '▲';
      case 'celo':
        return '◈';
      case 'cronos':
        return '◈';
      case 'gnosis':
        return '◈';
      case 'linea':
        return '◈';
      case 'mantle':
        return '◈';
      case 'moonbeam':
        return '◈';
      case 'scroll':
        return '◈';
      case 'zksync':
        return '◈';
      default:
        return '◈';
    }
  };

  // Don't render if team is loading or no team is selected
  if (teamLoading || !selectedTeam?.name) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: styles.backgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={32} sx={{ color: styles.primaryColor }} />
          <Typography
            sx={{
              ...styles.bodyFont,
              color: styles.textSecondary,
              mt: 2,
              fontSize: '0.875rem'
            }}
          >
            {teamLoading ? 'Loading team data...' : 'No team selected'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: styles.backgroundColor,
        py: 3
      }}
    >
      <Container maxWidth="xl">
        {/* Title */}
        {mode !== 'websites-only' && (
        <Typography
          variant="h5"
          sx={{
            ...styles.headingFont,
            color: styles.textPrimary,
            mb: 2
          }}
        >
          {mode === 'smart-contracts-only' ? 'Add Smart Contract' : 'Integrations'}
        </Typography>
        )}

        {/* Tab Navigation - hidden when mode is websites-only or smart-contracts-only */}
        {!mode && (
        <Box
          sx={{
            borderBottom: `1px solid ${styles.borderColor}`,
            mb: 1.5
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 0,
              padding: 0
            }}
          >
            {visibleTabs.includes('websites') && (
              <Box
                component="button"
                onClick={() => setActiveTab('websites')}
                sx={{
                  border: 'none',
                  outline: 'none',
                  borderRadius: 0,
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: 'transparent',
                  color: activeTab === 'websites' ? styles.primaryColor : styles.textSecondary,
                  position: 'relative',
                  borderBottom: activeTab === 'websites' ? `2px solid ${styles.primaryColor}` : '2px solid transparent',
                  marginBottom: '-1px',
                  ...styles.bodyFont,
                  '&:hover': {
                    color: styles.primaryColor
                  }
                }}
              >
                Websites
              </Box>
            )}
            {visibleTabs.includes('smart-contracts') && (
              <Box
                component="button"
                onClick={() => setActiveTab('smart-contracts')}
                sx={{
                  border: 'none',
                  outline: 'none',
                  borderRadius: 0,
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: 'transparent',
                  color: activeTab === 'smart-contracts' ? styles.primaryColor : styles.textSecondary,
                  position: 'relative',
                  borderBottom: activeTab === 'smart-contracts' ? `2px solid ${styles.primaryColor}` : '2px solid transparent',
                  marginBottom: '-1px',
                  ...styles.bodyFont,
                  '&:hover': {
                    color: styles.primaryColor
                  }
                }}
              >
                Smart Contracts
              </Box>
            )}
          </Box>
        </Box>
        )}

        {/* Grace Period Warning */}
        {inGracePeriod && (
          <Box sx={{ mb: 2 }}>
            <GracePeriodWarning gracePeriodInfo={gracePeriodInfo} />
          </Box>
        )}

        {/* Message Display */}
        {message && (
          <Alert
            severity={messageType === 'success' ? 'success' : 'error'}
            sx={{ mb: 2, borderRadius: '8px' }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        )}

        {/* Websites Tab */}
        {visibleTabs.includes('websites') && activeTab === 'websites' && (
          <Box
            sx={{
              border: `1px solid ${styles.borderColor}`,
              borderRadius: '8px',
              backgroundColor: styles.cardBg,
              mt: 2
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: `1px solid ${styles.borderColor}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  ...styles.headingFont,
                  color: styles.textPrimary,
                  fontSize: '1rem'
                }}
              >
                Your Websites
              </Typography>
              <Button
                variant="contained"
                onClick={() => setShowAddWebsiteModal(true)}
                startIcon={<Plus size={16} />}
                sx={{
                  ...styles.bodyFont,
                  background: '#4b57aa',
                  color: 'white',
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  px: 2,
                  py: 0.75,
                  boxShadow: 'none',
                  '&:hover': {
                    background: '#4b57aa',
                    opacity: 0.9,
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    background: '#DDE2EE',
                    color: '#475569'
                  }
                }}
              >
                Add Website
              </Button>
            </Box>

            <Box sx={{ p: 3 }}>
              {isLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: styles.primaryColor }} />
                  <Typography
                    sx={{
                      ...styles.bodyFont,
                      color: styles.textSecondary,
                      mt: 2,
                      fontSize: '0.875rem'
                    }}
                  >
                    Loading websites...
                  </Typography>
                </Box>
              ) : websiteArray.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Icon name="website" size={48} color={styles.textSecondary} style={{ margin: '0 auto' }} />
                  <Typography
                    sx={{
                      ...styles.headingFont,
                      color: styles.textPrimary,
                      mt: 2,
                      fontSize: '0.875rem'
                    }}
                  >
                    No websites
                  </Typography>
                  <Typography
                    sx={{
                      ...styles.bodyFont,
                      color: styles.textSecondary,
                      mt: 1,
                      fontSize: '0.875rem'
                    }}
                  >
                    Get started by adding your first website.
                  </Typography>
                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={() => setShowAddWebsiteModal(true)}
                      startIcon={<Plus size={16} />}
                      sx={{
                        ...styles.bodyFont,
                        background: '#4b57aa',
                        color: 'white',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        boxShadow: 'none',
                        '&:hover': {
                          background: '#4b57aa',
                          opacity: 0.9,
                          boxShadow: 'none'
                        },
                        '&:disabled': {
                          background: '#DDE2EE',
                          color: '#475569'
                        }
                      }}
                    >
                      Add Website
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  {websiteArray.map((website, index) => (
                    <Box
                      key={website.site_id || website.siteId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: index < websiteArray.length - 1 ? `1px solid ${styles.borderColor}` : 'none'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Icon name="website" size={24} color={styles.primaryColor} />
                        <Box>
                          <Typography
                            sx={{
                              ...styles.bodyFont,
                              color: styles.textPrimary,
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            {website.name || website.Name || website.domain || website.Domain}
                          </Typography>
                          <Typography
                            sx={{
                              ...styles.bodyFont,
                              color: styles.textSecondary,
                              fontSize: '0.75rem'
                            }}
                          >
                            {website.domain || website.Domain}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {website.isVerified ? (
                          <Chip
                            icon={<CheckCircle size={14} />}
                            label="Verified"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              backgroundColor: '#D1FAE5',
                              color: '#065F46',
                              fontWeight: 500,
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<XCircle size={14} />}
                            label="Unverified"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              backgroundColor: '#FEF3C7',
                              color: '#92400E',
                              fontWeight: 500,
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        )}
                        <Tooltip title="Copy script">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedWebsite(website);
                              setScriptModal(true);
                            }}
                            sx={{
                              color: styles.primaryColor,
                              '&:hover': {
                                backgroundColor: '#F9FAFB'
                              }
                            }}
                          >
                            <Copy size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete website">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteWebsite(website.site_id || website.siteId)}
                            disabled={deleteLoading}
                            sx={{
                              color: '#DC2626',
                              '&:hover': {
                                backgroundColor: '#FEE2E2'
                              },
                              '&:disabled': {
                                color: styles.textSecondary
                              }
                            }}
                          >
                            <Icon name="delete" size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Smart Contracts Tab */}
        {visibleTabs.includes('smart-contracts') && activeTab === 'smart-contracts' && (
          <Box
            sx={{
              border: `1px solid ${styles.borderColor}`,
              borderRadius: '8px',
              backgroundColor: styles.cardBg,
              mt: 2
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: `1px solid ${styles.borderColor}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  ...styles.headingFont,
                  color: styles.textPrimary,
                  fontSize: '1rem'
                }}
              >
                Smart Contracts
              </Typography>
              <Button
                variant="contained"
                onClick={() => setShowAddContractModal(true)}
                startIcon={<Plus size={16} />}
                sx={{
                  ...styles.bodyFont,
                  background: '#4b57aa',
                  color: 'white',
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  px: 2,
                  py: 0.75,
                  boxShadow: 'none',
                  '&:hover': {
                    background: '#4b57aa',
                    opacity: 0.9,
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    background: '#DDE2EE',
                    color: '#475569'
                  }
                }}
              >
                Add Smart Contract
              </Button>
            </Box>

            <Box sx={{ p: 3 }}>
              {/* Loading Progress */}
              {(addingContract || isFetchingTransactions) && (
                <Alert
                  severity="info"
                  icon={<Loader2 className="animate-spin" size={20} />}
                  sx={{
                    mb: 2,
                    borderRadius: '8px',
                    backgroundColor: '#EFF6FF',
                    border: '1px solid #BFDBFE'
                  }}
                >
                  <Typography
                    sx={{
                      ...styles.bodyFont,
                      color: styles.textPrimary,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      mb: 1
                    }}
                  >
                    {loadingStatus}
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      backgroundColor: '#DBEAFE',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      mb: 1
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        backgroundColor: '#3B82F6',
                        width: `${(loadingProgress.current / loadingProgress.total) * 100}%`,
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      ...styles.bodyFont,
                      color: styles.textSecondary,
                      fontSize: '0.75rem'
                    }}
                  >
                    {Math.round((loadingProgress.current / loadingProgress.total) * 100)}% complete
                  </Typography>
                </Alert>
              )}

              {smartContracts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Icon name="smartcontract" size={48} color={styles.textSecondary} style={{ margin: '0 auto' }} />
                  <Typography
                    sx={{
                      ...styles.headingFont,
                      color: styles.textPrimary,
                      mt: 2,
                      fontSize: '0.875rem'
                    }}
                  >
                    No smart contracts
                  </Typography>
                  <Typography
                    sx={{
                      ...styles.bodyFont,
                      color: styles.textSecondary,
                      mt: 1,
                      fontSize: '0.875rem'
                    }}
                  >
                    Get started by adding your first smart contract to track on-chain activity.
                  </Typography>
                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={() => setShowAddContractModal(true)}
                      startIcon={<Plus size={16} />}
                      sx={{
                        ...styles.bodyFont,
                        background: '#4b57aa',
                        color: 'white',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        boxShadow: 'none',
                        '&:hover': {
                          background: '#4b57aa',
                          opacity: 0.9,
                          boxShadow: 'none'
                        },
                        '&:disabled': {
                          background: '#DDE2EE',
                          color: '#475569'
                        }
                      }}
                    >
                      Add Smart Contract
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  {smartContracts.map((contract, index) => (
                    <Box
                      key={contract._id || contract.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: index < smartContracts.length - 1 ? `1px solid ${styles.borderColor}` : 'none'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: '#F3F4F6',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography sx={{ fontSize: '1rem' }}>
                            {getBlockchainIcon(contract.blockchain)}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              ...styles.bodyFont,
                              color: styles.textPrimary,
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            {contract.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={contract.blockchain}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                backgroundColor: '#F3F4F6',
                                color: styles.textPrimary,
                                fontWeight: 500,
                                borderRadius: '4px',
                                '& .MuiChip-label': {
                                  px: 0.75
                                }
                              }}
                            />
                            {contract.tokenSymbol && (
                              <Chip
                                label={contract.tokenSymbol}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  backgroundColor: '#DBEAFE',
                                  color: '#1E40AF',
                                  fontWeight: 500,
                                  borderRadius: '4px',
                                  '& .MuiChip-label': {
                                    px: 0.75
                                  }
                                }}
                              />
                            )}
                            {contract.address && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                                <Typography
                                  sx={{
                                    ...styles.bodyFont,
                                    color: styles.textSecondary,
                                    fontSize: '0.75rem',
                                    fontFamily: 'monospace'
                                  }}
                                >
                                  {contract.address.substring(0, 6)}...{contract.address.substring(contract.address.length - 4)}
                                </Typography>
                                <Tooltip title="Copy address">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      navigator.clipboard.writeText(contract.address);
                                      antdMessage.success('Address copied to clipboard!');
                                    }}
                                    sx={{
                                      color: styles.textSecondary,
                                      '&:hover': {
                                        backgroundColor: '#F9FAFB'
                                      }
                                    }}
                                  >
                                    <Copy size={12} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {contract.fetch_status === 'fetching' ? (
                          <Chip
                            icon={<Loader2 size={14} className="animate-spin" />}
                            label="Fetching"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              backgroundColor: '#DBEAFE',
                              color: '#1E40AF',
                              fontWeight: 500,
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        ) : contract.fetch_status === 'completed' ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                            <Chip
                              icon={<CheckCircle size={14} />}
                              label="Completed"
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                backgroundColor: '#D1FAE5',
                                color: '#065F46',
                                fontWeight: 500,
                                borderRadius: '4px',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                            {(contract.last_updated || contract.highest_block_number) && (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {contract.last_updated && (
                                  <Typography
                                    sx={{
                                      ...styles.bodyFont,
                                      color: styles.textSecondary,
                                      fontSize: '0.65rem'
                                    }}
                                  >
                                    Updated: {new Date(contract.last_updated).toLocaleString()}
                                  </Typography>
                                )}
                                {contract.highest_block_number && (
                                  <Typography
                                    sx={{
                                      ...styles.bodyFont,
                                      color: styles.textSecondary,
                                      fontSize: '0.65rem'
                                    }}
                                  >
                                    Block: {contract.highest_block_number.toLocaleString()}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        ) : contract.fetch_status === 'pending' ? (
                          <Chip
                            label="Pending"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              backgroundColor: '#FEF3C7',
                              color: '#92400E',
                              fontWeight: 500,
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        ) : contract.fetch_status === 'error' ? (
                          <Chip
                            label="Error"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              backgroundColor: '#FEE2E2',
                              color: '#991B1B',
                              fontWeight: 500,
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        ) : contract.fetch_status === 'stopped' ? (
                          <Chip
                            label="Stopped"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              backgroundColor: '#F3F4F6',
                              color: styles.textPrimary,
                              fontWeight: 500,
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        ) : (
                          <Chip
                            label={contract.fetch_status || 'Active'}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              backgroundColor: '#F3F4F6',
                              color: styles.textPrimary,
                              fontWeight: 500,
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        )}
                        <Tooltip title="Refresh transactions">
                          <IconButton
                            size="small"
                            onClick={() => fetchUpdatedTransactions(contract)}
                            sx={{
                              color: styles.primaryColor,
                              '&:hover': {
                                backgroundColor: '#F9FAFB'
                              }
                            }}
                          >
                            <RefreshCw size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={deleteLoading ? 'Deleting...' : 'Delete contract'}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSmartContract(contract.contractId || contract._id || contract.id)}
                            disabled={deleteLoading}
                            sx={{
                              color: '#DC2626',
                              '&:hover': {
                                backgroundColor: '#FEE2E2'
                              },
                              '&:disabled': {
                                color: styles.textSecondary
                              }
                            }}
                          >
                            {deleteLoading ? (
                              <RefreshCw size={16} className="animate-spin" />
                            ) : (
                              <Icon name="delete" size={16} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}


        {/* Add Website Modal */}
        <Dialog
          open={showAddWebsiteModal}
          onClose={() => {
            setShowAddWebsiteModal(false);
            setDomainProtocol('https');
          }}
          maxWidth={false}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              overflow: 'hidden',
              width: 1200,
              height: 720,
              maxWidth: 'calc(100vw - 2rem)',
              maxHeight: 'calc(100vh - 2rem)',
              position: 'relative',
              backgroundColor: '#f8fafc'
            }
          }}
        >
          {/* Exact same background as LoginForm */}
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(ellipse 85% 85% at 50% 50%, #ffffff 0%, rgba(255,255,255,0.97) 28%, rgba(255,201,164,0.2) 52%, rgba(137,121,255,0.25) 78%, rgba(137,121,255,0.3) 100%)'
              }}
            />
            <Box
              component={motion.div}
              sx={{
                position: 'absolute',
                top: 80,
                left: 40,
                width: 288,
                height: 288,
                borderRadius: '50%',
                bgcolor: 'rgba(137, 121, 255, 0.1)',
                filter: 'blur(48px)'
              }}
              animate={{ x: [0, 50, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
            />
            <Box
              component={motion.div}
              sx={{
                position: 'absolute',
                bottom: 80,
                right: 40,
                width: 384,
                height: 384,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 201, 164, 0.1)',
                filter: 'blur(48px)'
              }}
              animate={{ x: [0, -40, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
            />
            <Box
              component={motion.div}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 320,
                height: 320,
                marginLeft: -160,
                marginTop: -160,
                borderRadius: '50%',
                bgcolor: 'rgba(137, 121, 255, 0.05)',
                filter: 'blur(48px)'
              }}
              animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0.3,
                backgroundImage: 'radial-gradient(circle, #8979FF 1px, transparent 1px)',
                backgroundSize: '40px 40px',
                backgroundPosition: '0 0'
              }}
            />
          </Box>
          <Box sx={{ p: 4, position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100%' }}>
            <Box sx={{ maxWidth: 420, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, justifyContent: 'space-between' }}>
            <Box sx={{ width: '100%' }}>
            <DialogTitle
              sx={{
                ...styles.headingFont,
                color: styles.textPrimary,
                fontSize: '1.25rem',
                px: 0,
                pt: 0,
                textAlign: 'center',
                width: '100%'
              }}
            >
              Add New Website
            </DialogTitle>
            <DialogContent sx={{ px: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', flex: 1, justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 3, mb: 3, width: '100%' }}>
                <Box>
                  <Typography sx={{ ...styles.headingFont, fontSize: '0.95rem', color: styles.textPrimary, mb: 1, display: 'block' }}>
                    App Name <span style={{ color: '#dc2626' }}>*</span>
                  </Typography>
                  <TextField
                    value={newWebsiteName}
                    onChange={(e) => setNewWebsiteName(e.target.value)}
                    placeholder="My DeFi Protocol"
                    fullWidth
                    required
                    size="small"
                    inputProps={{ maxLength: 50 }}
                    helperText="This is just for you—name it whatever helps you identify it."
                    error={!!newWebsiteName && !/^[a-zA-Z0-9\s\-]+$/.test(newWebsiteName)}
                    FormHelperTextProps={{ sx: { ...styles.bodyFont, fontSize: '0.75rem', mt: 0.5 } }}
                    sx={{
                      ...styles.bodyFont,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                </Box>
                <FormControl fullWidth size="small" required>
                  <Typography sx={{ ...styles.headingFont, fontSize: '0.95rem', color: styles.textPrimary, mb: 1, display: 'block' }}>
                    Website Domain <span style={{ color: '#dc2626' }}>*</span>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0, '& .MuiOutlinedInput-root': { flex: 1 } }}>
                    <Select
                      value={domainProtocol}
                      onChange={(e) => setDomainProtocol(e.target.value)}
                      sx={{
                        ...styles.bodyFont,
                        minWidth: 120,
                        borderRadius: '6px 0 0 6px',
                        '& .MuiSelect-select': { py: 1.25, fontSize: '0.875rem' },
                        '& fieldset': { borderRight: 0 }
                      }}
                    >
                      <MenuItem value="https" sx={styles.bodyFont}>https://</MenuItem>
                      <MenuItem value="http" sx={styles.bodyFont}>http://</MenuItem>
                    </Select>
                    <TextField
                      value={newWebsiteDomain}
                      onChange={(e) => setNewWebsiteDomain(e.target.value)}
                      placeholder="app.myprotocol.xyz"
                      fullWidth
                      size="small"
                      error={!!newWebsiteDomain && /[\/\?\#]/.test(newWebsiteDomain)}
                      sx={{
                        ...styles.bodyFont,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '0 6px 6px 0',
                          fontSize: '0.875rem',
                          '& fieldset': { borderLeft: 0 }
                        }
                      }}
                    />
                  </Box>
                  {domainProtocol === 'http' && (
                    <FormHelperText sx={{ color: '#d97706', fontSize: '0.75rem', mt: 0.5 }}>
                      ⚠️ We recommend HTTPS for security
                    </FormHelperText>
                  )}
                  <FormHelperText sx={{ ...styles.bodyFont, fontSize: '0.75rem', mt: 0.5 }}>
                    Enter your primary domain. You can add more later.
                  </FormHelperText>
                </FormControl>
              </Box>
            </DialogContent>
            </Box>
            <DialogActions sx={{ p: 0, pt: 3, mt: 'auto', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => { setShowAddWebsiteModal(false); setDomainProtocol('https'); }}
                sx={{
                  ...styles.bodyFont,
                  color: styles.textSecondary,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddWebsite}
                disabled={isLoading}
                variant="contained"
                startIcon={isLoading ? null : <Plus size={16} />}
                sx={{
                  ...styles.bodyFont,
                  background: '#4b57aa',
                  color: 'white',
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  px: 2,
                  py: 0.75,
                  boxShadow: 'none',
                  '&:hover': {
                    background: '#4b57aa',
                    opacity: 0.9,
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    background: '#DDE2EE',
                    color: '#475569'
                  }
                }}
              >
                {isLoading ? 'Adding...' : 'Add Website'}
              </Button>
            </DialogActions>
            </Box>
          </Box>
        </Dialog>

        {/* Script Modal */}
        {scriptModal && selectedWebsite && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div 
              className="rounded-[20px] shadow-2xl overflow-hidden relative bg-[#f8fafc] flex flex-col shrink-0"
              style={{ width: 1200, height: 720, maxWidth: 'calc(100vw - 2rem)', maxHeight: 'calc(100vh - 2rem)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              {/* Same background as Add Website modal */}
              <div className="absolute inset-0 z-0 pointer-events-none">
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'radial-gradient(ellipse 85% 85% at 50% 50%, #ffffff 0%, rgba(255,255,255,0.97) 28%, rgba(255,201,164,0.2) 52%, rgba(137,121,255,0.25) 78%, rgba(137,121,255,0.3) 100%)'
                  }}
                />
                <Box
                  component={motion.div}
                  sx={{
                    position: 'absolute',
                    top: 80,
                    left: 40,
                    width: 288,
                    height: 288,
                    borderRadius: '50%',
                    bgcolor: 'rgba(137, 121, 255, 0.1)',
                    filter: 'blur(48px)'
                  }}
                  animate={{ x: [0, 50, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
                />
                <Box
                  component={motion.div}
                  sx={{
                    position: 'absolute',
                    bottom: 80,
                    right: 40,
                    width: 384,
                    height: 384,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 201, 164, 0.1)',
                    filter: 'blur(48px)'
                  }}
                  animate={{ x: [0, -40, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
                />
                <Box
                  component={motion.div}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: 320,
                    height: 320,
                    marginLeft: -160,
                    marginTop: -160,
                    borderRadius: '50%',
                    bgcolor: 'rgba(137, 121, 255, 0.05)',
                    filter: 'blur(48px)'
                  }}
                  animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
                  transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
                />
                <div
                  className="absolute inset-0 opacity-30"
                  style={{
                    backgroundImage: 'radial-gradient(circle, #8979FF 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                    backgroundPosition: '0 0'
                  }}
                />
              </div>
              <div className="relative z-10 p-6 overflow-y-auto flex-1 min-h-0 scrollbar-transparent">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: "'Raleway', sans-serif" }}>Install Tracking Script for {selectedWebsite.Name || selectedWebsite.Domain}</h3>
                  <button
                    onClick={() => setScriptModal(false)}
                    className="p-2 rounded-full hover:bg-gray-100 transition-all duration-200 text-[#475569]"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-[#475569] mb-6">
                  Choose your preferred installation method and follow the detailed steps below.
                </p>

                {/* Tab Navigation */}
                {(() => {
                  const siteId = selectedWebsite.site_id || selectedWebsite.siteId;
                  const cdnScript = getEmbedScriptSnippet(siteId);
                  const npmCode = `import Cryptique from 'cryptique-sdk';

// Initialize (call this once at app startup)
await Cryptique.init({
  siteId: '${siteId}',
  autoEvents: true,
  autoEventsDisabledPaths: [],
  autoEventsDisabledEvents: [],
});`;
                  return (
                <>
                <div className="border-b border-gray-200 mb-6 rounded-[8px] overflow-hidden">
                  <nav className="flex space-x-1 p-1 bg-transparent">
                    <button
                      onClick={() => setActiveScriptTab('cdn')}
                      className={`px-4 py-2 text-sm font-medium transition-all duration-200 rounded-[6px] ${
                        activeScriptTab === 'cdn'
                          ? 'bg-[#4b57aa] text-white'
                          : 'text-[#0f172a] hover:bg-[#DDE2EE]'
                      }`}
                    >
                      CDN
                    </button>
                    <button
                      onClick={() => setActiveScriptTab('tag-manager')}
                      className={`px-4 py-2 text-sm font-medium transition-all duration-200 rounded-[6px] ${
                        activeScriptTab === 'tag-manager'
                          ? 'bg-[#4b57aa] text-white'
                          : 'text-[#0f172a] hover:bg-[#DDE2EE]'
                      }`}
                    >
                      Tag Manager
                    </button>
                    <button
                      onClick={() => setActiveScriptTab('npm')}
                      className={`px-4 py-2 text-sm font-medium transition-all duration-200 rounded-[6px] ${
                        activeScriptTab === 'npm'
                          ? 'bg-[#4b57aa] text-white'
                          : 'text-[#0f172a] hover:bg-[#DDE2EE]'
                      }`}
                    >
                      NPM
                    </button>
                  </nav>
                </div>

                {/* CDN Tab */}
                {activeScriptTab === 'cdn' && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 1: Copy the code snippet</h4>
                        <div className="bg-gray-100 p-4 mb-3 rounded-[6px]">
                          <pre className="text-sm text-[#0f172a] whitespace-pre-wrap">{cdnScript}</pre>
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(cdnScript);
                            setMessage('Script copied to clipboard!');
                            setMessageType('success');
                          }}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-[#475569] bg-white hover:bg-gray-50 rounded-[6px]"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Script
                        </button>
                      </div>
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 2: Paste into your &lt;head&gt; tag</h4>
                        <p className="text-sm text-[#475569]">(Right before closing &lt;/head&gt;)</p>
                      </div>
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 3: Deploy and verify connection</h4>
                        <p className="text-sm text-[#475569] italic">Note: &quot;Changes will take effect immediately after deployment.&quot;</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tag Manager Tab */}
                {activeScriptTab === 'tag-manager' && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 1: Create Tag</h4>
                        <p className="text-sm text-[#475569]">In Google Tag Manager, go to Tags → New → Click Tag Configuration → Custom HTML</p>
                      </div>
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 2: Add Code</h4>
                        <p className="text-sm text-[#475569] mb-2">Paste this code into the Custom HTML field.</p>
                        <div className="bg-gray-100 p-4 mb-3 rounded-[6px]">
                          <pre className="text-sm text-[#0f172a] whitespace-pre-wrap">{cdnScript}</pre>
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(cdnScript);
                            setMessage('Script copied to clipboard!');
                            setMessageType('success');
                          }}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-[#475569] bg-white hover:bg-gray-50 rounded-[6px]"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Script
                        </button>
                      </div>
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 3: Set Trigger</h4>
                        <p className="text-sm text-[#475569]">Click Triggering → Choose a trigger → Select All Pages</p>
                      </div>
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 4: Save &amp; Publish</h4>
                        <p className="text-sm text-[#475569]">Click Save (name it &quot;Cryptique Analytics&quot;) → Click Preview and Verify</p>
                      </div>
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 5: Verify</h4>
                        <p className="text-sm text-[#475569]">Once published, click Next below to verify the connection.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* NPM Tab */}
                {activeScriptTab === 'npm' && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 1: Install Package</h4>
                        <div className="bg-gray-100 p-4 rounded-[6px]">
                          <pre className="text-sm text-[#0f172a]">npm install cryptique-sdk</pre>
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('npm install cryptique-sdk');
                            setMessage('Command copied to clipboard!');
                            setMessageType('success');
                          }}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-[#475569] bg-white hover:bg-gray-50 rounded-[6px] mt-2"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy
                        </button>
                      </div>
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 2: Initialize</h4>
                        <div className="bg-gray-100 p-4 mb-2 rounded-[6px]">
                          <pre className="text-sm text-[#0f172a] whitespace-pre-wrap">{npmCode}</pre>
                        </div>
                        <p className="text-sm text-[#475569] italic mb-2">Note: &quot;Add this to your app&apos;s entry point (e.g., App.tsx, main.js)&quot;</p>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(npmCode);
                            setMessage('Code copied to clipboard!');
                            setMessageType('success');
                          }}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-[#475569] bg-white hover:bg-gray-50 rounded-[6px]"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Code
                        </button>
                      </div>
                      <div className="border border-gray-200 p-4 rounded-[8px] bg-white/90">
                        <h4 className="text-md font-medium text-[#0f172a] mb-2">Step 3: Verify</h4>
                        <p className="text-sm text-[#475569]">Once published, click Next below to verify the connection.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                  <div className="text-sm text-[#475569]">
                    <strong>Site ID:</strong> {siteId}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleVerifyWebsite(siteId)}
                      disabled={verifyLoading}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium text-white rounded-[6px] disabled:opacity-50"
                      style={{ background: '#4b57aa' }}
                    >
                      {verifyLoading ? 'Verifying...' : 'Verify Installation'}
                    </button>
                    <button
                      onClick={() => setScriptModal(false)}
                      className="px-4 py-2 text-sm font-medium text-[#475569] bg-gray-100 border border-gray-300 hover:bg-gray-200 rounded-[6px]"
                    >
                      Close
                    </button>
                  </div>
                </div>
                </>
                );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Add Smart Contract Modal - same theme as Add Website */}
        <Dialog
          open={showAddContractModal}
          onClose={() => {
            setShowAddContractModal(false);
            resetContractForm();
          }}
          maxWidth={false}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              overflow: 'hidden',
              width: 1200,
              height: 720,
              maxWidth: 'calc(100vw - 2rem)',
              maxHeight: 'calc(100vh - 2rem)',
              position: 'relative',
              backgroundColor: '#f8fafc'
            }
          }}
        >
          {/* Same background as Add Website modal */}
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(ellipse 85% 85% at 50% 50%, #ffffff 0%, rgba(255,255,255,0.97) 28%, rgba(255,201,164,0.2) 52%, rgba(137,121,255,0.25) 78%, rgba(137,121,255,0.3) 100%)'
              }}
            />
            <Box
              component={motion.div}
              sx={{
                position: 'absolute',
                top: 80,
                left: 40,
                width: 288,
                height: 288,
                borderRadius: '50%',
                bgcolor: 'rgba(137, 121, 255, 0.1)',
                filter: 'blur(48px)'
              }}
              animate={{ x: [0, 50, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
            />
            <Box
              component={motion.div}
              sx={{
                position: 'absolute',
                bottom: 80,
                right: 40,
                width: 384,
                height: 384,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 201, 164, 0.1)',
                filter: 'blur(48px)'
              }}
              animate={{ x: [0, -40, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
            />
            <Box
              component={motion.div}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 320,
                height: 320,
                marginLeft: -160,
                marginTop: -160,
                borderRadius: '50%',
                bgcolor: 'rgba(137, 121, 255, 0.05)',
                filter: 'blur(48px)'
              }}
              animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0.3,
                backgroundImage: 'radial-gradient(circle, #8979FF 1px, transparent 1px)',
                backgroundSize: '40px 40px',
                backgroundPosition: '0 0'
              }}
            />
          </Box>
          <Box sx={{ p: 4, position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100%' }}>
            <Box sx={{ maxWidth: 520, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, justifyContent: 'flex-start', overflowY: 'auto' }} className="scrollbar-transparent">
              <Box sx={{ width: '100%' }}>
                <DialogTitle
                  sx={{
                    ...styles.headingFont,
                    color: styles.textPrimary,
                    fontSize: '1.25rem',
                    px: 0,
                    pt: 0,
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  Add Smart Contract
                </DialogTitle>
                <Typography sx={{ ...styles.bodyFont, fontSize: '0.875rem', color: styles.textSecondary, textAlign: 'center', mb: 3 }}>
                  Add a new smart contract to track on-chain activity and transactions.
                </Typography>

                {contractError && (
                  <Alert
                    severity="error"
                    sx={{ mb: 3, borderRadius: '8px' }}
                  >
                    {contractError}
                  </Alert>
                )}

                <DialogContent sx={{ px: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', flex: 1, justifyContent: 'flex-start' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, width: '100%' }}>
                    <Box>
                      <Typography sx={{ ...styles.headingFont, fontSize: '0.95rem', color: styles.textPrimary, mb: 1, display: 'block' }}>
                        Contract Address <span style={{ color: '#dc2626' }}>*</span>
                      </Typography>
                      <TextField
                        value={contractAddress}
                        onChange={(e) => setContractAddress(e.target.value)}
                        placeholder="0x..."
                        fullWidth
                        required
                        size="small"
                        sx={{
                          ...styles.bodyFont,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ ...styles.headingFont, fontSize: '0.95rem', color: styles.textPrimary, mb: 1, display: 'block' }}>
                        Contract Name <span style={{ color: '#dc2626' }}>*</span>
                      </Typography>
                      <TextField
                        value={contractName}
                        onChange={(e) => setContractName(e.target.value)}
                        placeholder="My Token Contract"
                        fullWidth
                        required
                        size="small"
                        sx={{
                          ...styles.bodyFont,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ ...styles.headingFont, fontSize: '0.95rem', color: styles.textPrimary, mb: 1, display: 'block' }}>
                          Blockchain
                        </Typography>
                        <Select
                          value={blockchain}
                          onChange={(e) => setBlockchain(e.target.value)}
                          fullWidth
                          size="small"
                          sx={{
                            ...styles.bodyFont,
                            borderRadius: '6px',
                            '& .MuiSelect-select': { py: 1.25, fontSize: '0.875rem' }
                          }}
                        >
                          <MenuItem value="Ethereum" sx={styles.bodyFont}>Ethereum</MenuItem>
                          <MenuItem value="BNB Smart Chain" sx={styles.bodyFont}>BNB Smart Chain</MenuItem>
                          <MenuItem value="Base" sx={styles.bodyFont}>Base</MenuItem>
                          <MenuItem value="Polygon" sx={styles.bodyFont}>Polygon</MenuItem>
                          <MenuItem value="Arbitrum One" sx={styles.bodyFont}>Arbitrum One</MenuItem>
                          <MenuItem value="OP" sx={styles.bodyFont}>OP (Optimism)</MenuItem>
                          <MenuItem value="Avalanche C-Chain" sx={styles.bodyFont}>Avalanche C-Chain</MenuItem>
                          <MenuItem value="Celo" sx={styles.bodyFont}>Celo</MenuItem>
                          <MenuItem value="Gnosis" sx={styles.bodyFont}>Gnosis</MenuItem>
                          <MenuItem value="Linea" sx={styles.bodyFont}>Linea</MenuItem>
                          <MenuItem value="Mantle" sx={styles.bodyFont}>Mantle</MenuItem>
                          <MenuItem value="Moonbeam" sx={styles.bodyFont}>Moonbeam</MenuItem>
                          <MenuItem value="Scroll" sx={styles.bodyFont}>Scroll</MenuItem>
                          <MenuItem value="Soneium" sx={styles.bodyFont}>Soneium</MenuItem>
                          <MenuItem value="zkSync" sx={styles.bodyFont}>zkSync</MenuItem>
                          <MenuItem value="Abstract" sx={styles.bodyFont}>Abstract</MenuItem>
                          <MenuItem value="ApeChain" sx={styles.bodyFont}>ApeChain</MenuItem>
                          <MenuItem value="Arbitrum Nova" sx={styles.bodyFont}>Arbitrum Nova</MenuItem>
                          <MenuItem value="Berachain" sx={styles.bodyFont}>Berachain</MenuItem>
                          <MenuItem value="Blast" sx={styles.bodyFont}>Blast</MenuItem>
                          <MenuItem value="Fraxtal" sx={styles.bodyFont}>Fraxtal</MenuItem>
                          <MenuItem value="HyperEVM" sx={styles.bodyFont}>HyperEVM</MenuItem>
                          <MenuItem value="Katana" sx={styles.bodyFont}>Katana</MenuItem>
                          <MenuItem value="Monad" sx={styles.bodyFont}>Monad</MenuItem>
                          <MenuItem value="Moonriver" sx={styles.bodyFont}>Moonriver</MenuItem>
                          <MenuItem value="opBNB" sx={styles.bodyFont}>opBNB</MenuItem>
                          <MenuItem value="Sei" sx={styles.bodyFont}>Sei</MenuItem>
                          <MenuItem value="Sonic" sx={styles.bodyFont}>Sonic</MenuItem>
                          <MenuItem value="Stable" sx={styles.bodyFont}>Stable</MenuItem>
                          <MenuItem value="Swellchain" sx={styles.bodyFont}>Swellchain</MenuItem>
                          <MenuItem value="Taiko" sx={styles.bodyFont}>Taiko</MenuItem>
                          <MenuItem value="Unichain" sx={styles.bodyFont}>Unichain</MenuItem>
                          <MenuItem value="World" sx={styles.bodyFont}>World</MenuItem>
                          <MenuItem value="XDC" sx={styles.bodyFont}>XDC</MenuItem>
                        </Select>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ ...styles.headingFont, fontSize: '0.95rem', color: styles.textPrimary, mb: 1, display: 'block' }}>
                          Token Symbol
                        </Typography>
                        <TextField
                          value={tokenSymbol}
                          onChange={(e) => setTokenSymbol(e.target.value)}
                          placeholder="USDT"
                          fullWidth
                          size="small"
                          sx={{
                            ...styles.bodyFont,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Typography sx={{ ...styles.headingFont, fontSize: '0.95rem', color: styles.textPrimary, mb: 1, display: 'block' }}>
                        Contract Type
                      </Typography>
                      <Select
                        value={contractType}
                        onChange={(e) => setContractType(e.target.value)}
                        fullWidth
                        size="small"
                        sx={{
                          ...styles.bodyFont,
                          borderRadius: '6px',
                          '& .MuiSelect-select': { py: 1.25, fontSize: '0.875rem' }
                        }}
                      >
                        <MenuItem value="ERC-20" sx={styles.bodyFont}>ERC-20 (18 Decimal Places)</MenuItem>
                        <MenuItem value="StableCoin" sx={styles.bodyFont}>StableCoin (6 Decimal Places)</MenuItem>
                        <MenuItem value="Bitcoin" sx={styles.bodyFont}>Bitcoin (8 Decimal Places)</MenuItem>
                        <MenuItem value="Other" sx={styles.bodyFont}>Other (Custom Decimal Places)</MenuItem>
                      </Select>
                    </Box>
                    {contractType === 'Other' && (
                      <Box>
                        <Typography sx={{ ...styles.headingFont, fontSize: '0.95rem', color: styles.textPrimary, mb: 1, display: 'block' }}>
                          Custom Decimal Places
                        </Typography>
                        <TextField
                          type="number"
                          value={customDecimals}
                          onChange={(e) => setCustomDecimals(e.target.value)}
                          placeholder="18"
                          fullWidth
                          size="small"
                          inputProps={{ min: 0 }}
                          sx={{
                            ...styles.bodyFont,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </DialogContent>
                <DialogActions sx={{ p: 0, pt: 3, mt: 3, gap: 1, width: '100%', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => { setShowAddContractModal(false); resetContractForm(); }}
                    sx={{
                      ...styles.bodyFont,
                      color: styles.textSecondary,
                      textTransform: 'none',
                      fontSize: '0.875rem'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddSmartContract}
                    disabled={addingContract}
                    variant="contained"
                    startIcon={addingContract ? null : <Plus size={16} />}
                    sx={{
                      ...styles.bodyFont,
                      background: '#4b57aa',
                      color: 'white',
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      px: 2,
                      py: 0.75,
                      boxShadow: 'none',
                      '&:hover': {
                        background: '#4b57aa',
                        opacity: 0.9,
                        boxShadow: 'none'
                      },
                      '&:disabled': {
                        background: '#DDE2EE',
                        color: '#475569'
                      }
                    }}
                  >
                    {addingContract ? 'Adding...' : 'Add Contract'}
                  </Button>
                </DialogActions>
              </Box>
            </Box>
          </Box>
        </Dialog>

      </Container>

      {/* Delete Smart Contract Modal - same design as Delete Website */}
      {deleteModalVisible && contractToDelete && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={(e) => e.target === e.currentTarget && handleDeleteCancel()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-contract-modal-title"
        >
          <div
            className="rounded-xl shadow-xl border max-w-md w-full p-6"
            style={{
              backgroundColor: '#ffffff',
              borderColor: '#DDE2EE',
              fontFamily: 'Raleway, sans-serif',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="delete-contract-modal-title"
              className="text-lg font-semibold mb-2"
              style={{ color: '#0f172a' }}
            >
              Delete Smart Contract
            </h2>
            <p className="text-sm mb-6" style={{ color: '#475569' }}>
              {contractToDelete.isFetching 
                ? `This contract "${contractToDelete.contractName || contractToDelete.name || 'contract'}" is currently fetching transactions. Are you sure you want to delete it? The fetch will be cancelled.`
                : `Are you sure you want to delete the smart contract "${contractToDelete.contractName || contractToDelete.name || 'contract'}"? This will permanently delete the contract and all its associated data. This action cannot be undone.`
              }
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDeleteCancel}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  color: '#475569',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #DDE2EE',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                style={{
                  color: '#fff',
                  backgroundColor: '#dc2626',
                  border: 'none',
                }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Website Modal - same design as Delete chart / Delete Board */}
      {deleteWebsiteModalVisible && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={(e) => e.target === e.currentTarget && handleDeleteWebsiteCancel()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-website-modal-title"
        >
          <div
            className="rounded-xl shadow-xl border max-w-md w-full p-6"
            style={{
              backgroundColor: '#ffffff',
              borderColor: '#DDE2EE',
              fontFamily: 'Raleway, sans-serif',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="delete-website-modal-title"
              className="text-lg font-semibold mb-2"
              style={{ color: '#0f172a' }}
            >
              Delete Website
            </h2>
            <p className="text-sm mb-6" style={{ color: '#475569' }}>
              Are you sure you want to delete the website &quot;{websiteToDelete?.name || websiteToDelete?.domain || 'website'}&quot;? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDeleteWebsiteCancel}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  color: '#475569',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #DDE2EE',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteWebsiteConfirm}
                disabled={deletingWebsite}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                style={{
                  color: '#fff',
                  backgroundColor: '#dc2626',
                  border: 'none',
                }}
              >
                {deletingWebsite ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default IntegrationManager;
